import { useEffect, useRef, useState } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import cn from "classnames";
import { INPUT_FIELD_NAMES } from "constants/fieldNames.constans";
import { useClickOutside } from "hooks/useClickOutside";
import { Country } from "types/country.types";
import type { FormValues } from "types/form.types";
import { ErrorMessage } from "../ErrorMessage";
import { Input } from "../Input";
import { PhoneCode } from "../PhoneCode/PhoneCode";
import { PhoneSelectList } from "../PhoneSelectList";
import style from "./PhoneSelect.module.scss";

type PhoneSelectProps = {
  userCountry: Country;
  className?: string;
  isCountryLoading: boolean;
  rules?: RegisterOptions<FormValues, typeof INPUT_FIELD_NAMES.phoneNumber>;
  disabled?: boolean;
};

export const PhoneSelect = ({
  userCountry,
  className,
  isCountryLoading,
  rules,
  disabled = false,
}: PhoneSelectProps) => {
  const [isOpenList, setIsOpenList] = useState<boolean>(false);
  const phoneSelectRef = useRef<HTMLDivElement>(null);
  const [countryData, setCountryData] = useState<Country>({
    name: "",
    iso: "",
    countryCode: "",
  });

  const { control, setValue } = useFormContext<FormValues>();

  useClickOutside(phoneSelectRef, () => setIsOpenList(false));
  useEffect(() => setCountryData(userCountry), [userCountry]);

  const onCountryCodeChange = (country: Country) => {
    setCountryData(country);
    setIsOpenList(false);
    setValue(INPUT_FIELD_NAMES.phoneDialCode, { name: country.name, value: country.iso });
    setValue(INPUT_FIELD_NAMES.phoneNumber, "");
  };

  useEffect(() => {
    if (!countryData.iso) {
      return;
    }

    setValue(INPUT_FIELD_NAMES.phoneDialCode, { name: countryData.name, value: countryData.iso });
  }, [countryData, setValue]);

  const onMainNumberChange = (value: string, onChange: (value: string) => void) => {
    const number = value.replace(/\D/g, "");

    if (countryData && number.length + countryData.iso.length <= 16) {
      onChange(number);
    }
  };

  return (
    <div ref={phoneSelectRef} className={className}>
      <Controller
        name={INPUT_FIELD_NAMES.phoneNumber}
        rules={rules}
        control={control}
        render={({ field: { name, value, onChange, onBlur }, fieldState: { error } }) => (
          <div className={style.phoneSelectWrapper}>
            <div className={style.phoneSelectLableWrapper}>
              <label className={style.phoneSelectLabel} htmlFor={name}>
                Phone number
              </label>
            </div>
            <div className={style.phoneSelectInputWrapper}>
              <div
                className={cn(style.phoneFieldShell, {
                  [style["phoneFieldShell--invalid"]]: error,
                  [style.phoneFieldShellDisabled]: disabled,
                })}
              >
                <PhoneCode
                  countryData={countryData}
                  isOpenList={isOpenList}
                  setIsOpenList={setIsOpenList}
                  isCountryLoading={isCountryLoading}
                  disabled={disabled}
                />
                <div
                  className={cn(style.phoneFieldDivider, { [style.phoneFieldDividerDisabled]: disabled })}
                  aria-hidden
                />
                <div className={style.phoneFieldInputWrap}>
                  <Input
                    id={name}
                    name={name}
                    type="tel"
                    embedded
                    disabled={disabled}
                    placeholder="(000) 00 00 000"
                    onBlur={onBlur}
                    onChange={(e) => onMainNumberChange(e.currentTarget.value, onChange)}
                    value={value || ""}
                    className={className}
                  />
                </div>
                {isOpenList && !disabled && <PhoneSelectList onCountryCodeChange={onCountryCodeChange} />}
              </div>
              <ErrorMessage error={error} />
            </div>
          </div>
        )}
      />
    </div>
  );
};
